import { Box, Button, Link, Stack } from '@mui/material';
import { getGridSingleSelectOperators, GridColDef } from '@mui/x-data-grid';
import { LabLostAndFoundUpload } from 'api/labs';
import { getReadOnlyDateTimeFilterOperators } from 'components/atoms/BaseDataGrid/ReadOnlyDataGrid';
import CopyableText from 'components/atoms/CopyableText';
import { SlidePreviewCell } from 'components/atoms/DataGridRenderers/SlideIdCellRenderer';
import { UploadFileCell } from 'components/atoms/DataGridRenderers/UploadFileCell';
import { HighlightedText } from 'components/atoms/HighlightedText';
import {
  getSlideIntakeLifecycle,
  SlideIntakeLifecyclePresentation,
  SlideIntakeRequestState,
} from 'components/SlideIntake/lifecycle';
import { JobsTab } from 'components/StudyDashboard/useStudyDashboardTabState';
import { Slide } from 'interfaces/slide';
import { IngestJobSummary } from 'interfaces/ingestJob';
import { Study } from 'interfaces/study';
import { filter, map, size } from 'lodash';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { BooleanParam, encodeQueryParams, JsonParam, StringParam } from 'use-query-params';
import { formatDateTimeForUser, toDate } from 'utils/dateTime';
import { formatBytes } from 'utils/helpers';
import { stringify } from 'qs';

import { SOURCE_STUDY_GRID_FIELD } from './fields';
import IngestJobStatusCell from '../UploadsDashboard/IngestJobStatusCell';

const gridCellContainerSx = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
};

const getFileName = (objectKey: string) => objectKey.split('/').pop() || objectKey;
const getDisplayedPath = (objectKey: string) => objectKey.replace(/^ATOM\//, '').replace(/\/[^/]+$/, '');
const getFileType = (objectKey: string) => {
  const fileName = getFileName(objectKey);
  const extensionSeparatorIndex = fileName.lastIndexOf('.');

  return extensionSeparatorIndex > 0 ? fileName.slice(extensionSeparatorIndex + 1).toLowerCase() : 'No extension';
};

const getUploadsDashboardUrl = (labId: string, studyId: string) => {
  const encodedQuery = encodeQueryParams({ filters: JsonParam, labId: StringParam }, { filters: { studyId }, labId });

  return `/uploads-dashboard?${stringify(encodedQuery)}`;
};

const getJobUrl = (labId: string, row: LabLostAndFoundUpload, jobId: string, archived?: true) => {
  const encodedQuery = encodeQueryParams(
    {
      filters: JsonParam,
      labId: StringParam,
      studyDashboardTab: StringParam,
      jobDrawerOpen: BooleanParam,
      currentJobId: StringParam,
      archived: BooleanParam,
    },
    {
      filters: { studyId: row.sourceStudyId },
      labId,
      studyDashboardTab: JobsTab,
      jobDrawerOpen: true,
      currentJobId: jobId,
      archived,
    }
  );

  return `/procedures?${stringify(encodedQuery)}`;
};

interface LostUploadStatus extends SlideIntakeLifecyclePresentation {
  archived?: true;
  job: IngestJobSummary | null;
}

const getStatus = (row: LabLostAndFoundUpload, requestState: SlideIntakeRequestState): LostUploadStatus => {
  const lifecycle = getSlideIntakeLifecycle({
    requestState,
    fileKind: 'slide',
    hasRejectedIngest: Boolean(row.latestSuccessfulIngestJobArchived),
    hasActiveIngest: Boolean(row.activeIngestJob),
    hasFailedIngest: Boolean(row.missingIngest && row.latestFailedIngestJob),
    hasUsableSuccessfulIngest: Boolean(
      !row.missingIngest &&
        !row.latestSuccessfulIngestJobArchived &&
        (row.resolvedSlideId || row.latestSuccessfulIngestJob || row.activeSuccessfulIngestJob)
    ),
    hasCaseAssignment: size(row.cases) > 0,
  });

  if (lifecycle.state === 'ingesting') {
    return { ...lifecycle, job: row.activeIngestJob };
  }
  if (lifecycle.state === 'ingestFailed') {
    return { ...lifecycle, job: row.latestFailedIngestJob };
  }
  if (lifecycle.state === 'rejectedIngest') {
    return {
      ...lifecycle,
      job: row.latestSuccessfulIngestJob || row.latestIngestJob,
      archived: true,
    };
  }
  if (lifecycle.state === 'queuedForIngest') {
    return { ...lifecycle, job: row.latestIngestJob };
  }

  return {
    ...lifecycle,
    job: row.latestSuccessfulIngestJob || row.latestIngestJob,
  };
};

interface UseLostAndFoundColumnsOptions {
  canApplyCaseManifest: boolean;
  canAssignToTargetStudy: boolean;
  canExecuteUploadIngest: boolean;
  canIgnoreUploads: boolean;
  ignoreErrorsByObjectKey: Record<string, string>;
  labId: string;
  onAssignUpload: (row: LabLostAndFoundUpload) => void;
  onExecuteUploadIngest: (row: LabLostAndFoundUpload) => void;
  onIgnoreUpload: (row: LabLostAndFoundUpload) => void;
  pendingIgnoreObjectKeys: string[];
  quickFilterQuery?: string;
  studies: Study[];
  lifecycleRequestState?: SlideIntakeRequestState;
}

export const useLostAndFoundColumns = ({
  canApplyCaseManifest,
  canAssignToTargetStudy,
  canExecuteUploadIngest,
  canIgnoreUploads,
  ignoreErrorsByObjectKey,
  labId,
  onAssignUpload,
  onExecuteUploadIngest,
  onIgnoreUpload,
  pendingIgnoreObjectKeys,
  quickFilterQuery,
  studies,
  lifecycleRequestState = 'ready',
}: UseLostAndFoundColumnsOptions): GridColDef<LabLostAndFoundUpload>[] => {
  const singleSelectOperators = filter(getGridSingleSelectOperators(), ({ value }) =>
    ['is', 'isAnyOf'].includes(value)
  );

  return React.useMemo(
    () => [
      {
        field: 'objectKey',
        headerName: 'Name',
        minWidth: 280,
        flex: 1.2,
        filterable: false,
        valueGetter: (_value, row) => `${getFileName(row.objectKey)} ${getDisplayedPath(row.objectKey)}`,
        renderCell: ({ row }) => (
          <UploadFileCell
            copyAriaLabel={`Copy S3 path for ${getFileName(row.objectKey)}`}
            copyTooltip="Copy S3 path"
            displayName={getFileName(row.objectKey)}
            displayedPath={getDisplayedPath(row.objectKey)}
            error={ignoreErrorsByObjectKey[row.objectKey]}
            fullPath={row.objectKey}
            query={quickFilterQuery}
          />
        ),
      },
      {
        field: 'type',
        headerName: 'Type',
        minWidth: 110,
        valueGetter: (_value, row) => getFileType(row.objectKey),
        renderCell: ({ row }) => (
          <Box sx={gridCellContainerSx}>
            <HighlightedText value={getFileType(row.objectKey)} query={quickFilterQuery} />
          </Box>
        ),
      },
      {
        field: 'size',
        headerName: 'Size',
        type: 'number',
        minWidth: 110,
        filterable: false,
        renderCell: ({ row }) => (
          <Box sx={gridCellContainerSx}>
            <HighlightedText value={formatBytes(row.size)} query={quickFilterQuery} />
          </Box>
        ),
      },
      {
        field: 'uploadedAt',
        headerName: 'Upload date',
        minWidth: 190,
        type: 'dateTime',
        filterOperators: getReadOnlyDateTimeFilterOperators(),
        valueGetter: (value) => toDate(value),
        renderCell: ({ row }) => (
          <Box sx={gridCellContainerSx}>
            <HighlightedText value={formatDateTimeForUser(row.uploadedAt)} query={quickFilterQuery} />
          </Box>
        ),
      },
      {
        field: SOURCE_STUDY_GRID_FIELD,
        headerName: 'Source study',
        minWidth: 190,
        flex: 0.7,
        type: 'singleSelect',
        filterOperators: singleSelectOperators,
        valueGetter: (_value, row) => row.sourceStudyName,
        valueOptions: map(studies, ({ id, name }) => ({ value: id, label: name })),
        renderCell: ({ row }) => (
          <Link component={RouterLink} to={getUploadsDashboardUrl(labId, row.sourceStudyId)} underline="hover">
            <HighlightedText value={row.sourceStudyName} query={quickFilterQuery} />
          </Link>
        ),
      },
      {
        field: 'status',
        headerName: 'Ingest status',
        minWidth: 260,
        flex: 1,
        filterable: false,
        valueGetter: (_value, row) => {
          const status = getStatus(row, lifecycleRequestState);
          return [status.job?.startedAt, status.job?.status, status.job?.statusMessage, status.job?.id]
            .filter(Boolean)
            .join(' ');
        },
        renderCell: ({ row }) => {
          const status = getStatus(row, lifecycleRequestState);
          const rowLabel = getFileName(row.objectKey);

          return (
            <Box sx={gridCellContainerSx}>
              <IngestJobStatusCell
                job={status.job}
                jobUrl={status.job ? getJobUrl(labId, row, status.job.id, status.archived) : null}
                rowLabel={rowLabel}
              />
            </Box>
          );
        },
      },
      {
        field: 'resolvedSlideId',
        headerName: 'Slide',
        minWidth: 250,
        flex: 1,
        filterable: false,
        valueGetter: (_value, row) => row.resolvedSlideId || '',
        renderCell: ({ row }) => {
          if (!row.resolvedSlideId) {
            return <Box sx={gridCellContainerSx}>—</Box>;
          }
          const slides: Slide[] = [{ id: row.resolvedSlideId, labId, originalFileName: getFileName(row.objectKey) }];

          return (
            <Box sx={gridCellContainerSx}>
              <SlidePreviewCell
                slides={slides}
                labId={labId}
                caseStudyId={row.sourceStudyId}
                summaryContent={
                  <CopyableText
                    variant="caption"
                    textForCopy={row.resolvedSlideId}
                    tooltipText="Copy slide ID"
                    copyWithButton="right"
                    copyButtonProps={{
                      size: 'small',
                      edge: 'end',
                      'aria-label': `Copy slide ID for ${getFileName(row.objectKey)}`,
                    }}
                    copyIconProps={{ fontSize: 'small' }}
                  >
                    {row.resolvedSlideId}
                  </CopyableText>
                }
              />
            </Box>
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Action',
        minWidth: 220,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const rowLabel = getFileName(row.objectKey);
          const isIgnorePending = pendingIgnoreObjectKeys.includes(row.objectKey);
          const lifecycle = getStatus(row, lifecycleRequestState);
          const canShowAssignAction = lifecycle.actionCategory === 'organizeSlides';
          const canShowIngestAction = ['retryIngest', 'trackIngest'].includes(lifecycle.actionCategory);

          return (
            <Box sx={gridCellContainerSx}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {canApplyCaseManifest && canShowAssignAction && row.resolvedSlideId ? (
                  <Button
                    size="small"
                    disabled={!canAssignToTargetStudy}
                    onClick={() => onAssignUpload(row)}
                    aria-label={`Organize ${rowLabel}`}
                  >
                    Organize slides
                  </Button>
                ) : null}
                {canExecuteUploadIngest && canShowIngestAction ? (
                  <Button
                    size="small"
                    onClick={() => onExecuteUploadIngest(row)}
                    aria-label={`${lifecycle.actionCategory === 'retryIngest' ? 'Retry ingest' : 'Ingest'} ${rowLabel}`}
                  >
                    {lifecycle.actionCategory === 'retryIngest' ? 'Retry ingest' : 'Ingest'}
                  </Button>
                ) : null}
                {canIgnoreUploads ? (
                  <Button
                    size="small"
                    color="warning"
                    disabled={isIgnorePending}
                    onClick={() => onIgnoreUpload(row)}
                    aria-label={`Ignore ${rowLabel}`}
                  >
                    {isIgnorePending ? 'Ignoring...' : 'Ignore'}
                  </Button>
                ) : null}
              </Stack>
            </Box>
          );
        },
      },
    ],
    [
      canApplyCaseManifest,
      canAssignToTargetStudy,
      canExecuteUploadIngest,
      canIgnoreUploads,
      ignoreErrorsByObjectKey,
      labId,
      lifecycleRequestState,
      onAssignUpload,
      onExecuteUploadIngest,
      onIgnoreUpload,
      pendingIgnoreObjectKeys,
      quickFilterQuery,
      singleSelectOperators,
      studies,
    ]
  );
};
